import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from "react";
import axios from "axios";
import { URL } from "../common/constant";
import { ErrorModal } from "./successModal";

import appStore from "../assets/images/app-store.png";
import playStore from "../assets/images/google-play.png";
import PlayButton from "../assets/images/PlayButton.svg";
import uiScanIcon from "../assets/images/UI_Scanicon.svg";
import mail from "../assets/images/Mail.svg";
import call from "../assets/images/Call.svg";
import qrcode from "../assets/images/NDIlogobg.png";

const DialogueBox = forwardRef((props, ref) => {
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [dialogueVisible, setDialogueVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [qrCode, setQrCode] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [deepLinkUrl, setDeepLinkUrl] = useState("");
    const [threadId, setThreadId] = useState("");
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (threadId) {
            setupWebSocket();
        }
    }, [threadId]);

    useImperativeHandle(ref, () => ({
        openDialogue: () => {
            setDialogueVisible(true);
            setLoading(true);
            fetchQrCode();
        },
        closeDialogue: () => {
            setDialogueVisible(false);
        },
    }));

    const fetchQrCode = () => {
        axios
            .post(`${URL}/ndi/authenticate-ndi/`, { action: props.action || "login", })
            .then((response) => {
                const data = response.data;
                setThreadId(data.thread_id);
                sessionStorage.setItem("thread_id", data.thread_id);

                if (data.error) {
                    clearUserData();
                    setErrorMessage(
                        "Failed to generate QR code. Please try again."
                    );
                } else if (
                    data.qr_code_image &&
                    data.deep_link_url &&
                    data.thread_id
                ) {
                    setQrCode(`data:image/png;base64,${data.qr_code_image}`);
                    setDeepLinkUrl(data.deep_link_url);
                    setLoading(false);
                    const logo = document.getElementById("ndi-logo");
                    logo.style.display = "block";
                } else {
                    localStorage.removeItem("token", "TOKEN " + data.token);
                    clearUserData();
                    setErrorMessage(
                        "Failed to load QR code. Please try again."
                    );
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching QR code:", error);
                setErrorMessage("Error fetching QR code. Please try again.");
                setLoading(false);
            });
    };

    const setupWebSocket = () => {
        const storedThreadId = sessionStorage.getItem("thread_id");

        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        //const socketUrl = `${protocol}//127.0.0.1:8000/ws/socket-server/`;
        let socketUrl = `${protocol}//${window.location.host}/ws/socket-server/`;

        const socket = new WebSocket(socketUrl);

        socket.onmessage = (e) => {
            const data = JSON.parse(e.data);

            if (!data.thread_id || storedThreadId !== data.thread_id) {
                localStorage.removeItem("token", "TOKEN " + data.token);
                clearUserData();
                return;
            }

            switch (data.type) {
                case "Webhook processing complete":
                    handleWebhookProcessingComplete(data);
                    break;
                case "Webhook rejected":
                case "Webhook no data found":
                    handleWebhookRejected(storedThreadId, data.thread_id);
                    break;
                default:
                    console.log("Unknown data type:", data.type);
            }
        };

        // Cleanup WebSocket connection on component unmount
        return () => socket.close();
    };

    const handleLogin = (data) => {
        localStorage.setItem("token", "TOKEN " + data.token);
        clearUserData();

        window.location.href = "/";
    };

    const handleWebhookProcessingComplete = (data) => {
        axios
            .post(`${URL}/accounts/login/`, {
                username: data.cid_no,
                password: data.mobile_no,
            })
            .then((response) => {
                handleLogin(response.data);
            })
            .catch((error) => {
                localStorage.removeItem("token", "TOKEN " + data.token);
                clearUserData();
                handleLoginFailure(error);
            });
    };

    // Handling login failure
    const handleLoginFailure = (error) => {
        setLoading(false);
        setShowModal(true);

        if (error.response) {
            if (error.response.status === 400) {
                clearUserData();
                setModalContent(
                    "Account doesn't exist - Please check your CID/Mobile."
                );
            } else {
                clearUserData();
                setModalContent(JSON.stringify(error.response.data));
            }
        } else {
            clearUserData();
            setModalContent(
                "Oops, something went wrong. Check your internet or try again later."
            );
        }
    };

    const handleWebhookRejected = (storedThreadId, backend_thread_id) => {
        if (storedThreadId === backend_thread_id) {
            clearUserData();
            alert("Data Share Rejected");
            window.location.href = "/customer-login";
        } else {
            clearUserData();
            return;
        }
    };
    const clearUserData = () => {
        localStorage.removeItem("cid_no");
        localStorage.removeItem("mobile_no");
        localStorage.removeItem("full_name");
        localStorage.removeItem("email_address");
        localStorage.removeItem("village");
        localStorage.removeItem("gewog");
        localStorage.removeItem("dzongkhag");
        sessionStorage.clear();
    };

    const styles = {
        dialogueBox: {
            display: dialogueVisible ? "flex" : "none",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            padding: isMobile ? "10px" : "0",
        },
        dialogueContent: {
            backgroundColor: "white",
            padding: isMobile ? "15px" : "20px",
            borderRadius: "10px",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.25)",
            textAlign: "center",
            width: isMobile ? "95vw" : "80vw",
            maxWidth: "500px",
            maxHeight: isMobile ? "85vh" : "90vh", // Responsive max height
            height: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflowY: "auto",
            overflowX: "hidden",
        },        
        qrCodeContainer: {
            marginTop: "30px",
            width: "100%",
            maxWidth: isMobile ? "150px" : "180px",
            height: isMobile ? "130px" : "150px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
        },
        qrCodeImage: {
            width: "100%",
            height: "auto",
            border: "2px solid #5AC994",
            borderRadius: "10px",
        },
        smallImage: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "30px" : "40px",
            height: "auto",
            display: "none",
        },
        closeButton: {
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
            color: "#fff",
            height: "40px",
            width: "40px",
            fontSize: "20px",
            lineHeight: "40px",
            textAlign: "center",
            borderRadius: "50%",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
        },
        infoContainer: {
            marginTop: "30px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            textAlign: "left",
            padding: "8px",
            width: "100%",
            maxWidth: "350px",
            color: "gray",
            height: "auto",
            fontWeight: "500",
        },
        infoOrder: {
            marginBottom: "0px"
        },
        videoGuide: {
            marginTop: "30px",
            width: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "50px",
            padding: "5px 10px",
            border: "2px solid #5AC994",
            flexWrap: "wrap",
        },
        videoGuideLink: {
            textDecoration: "none",
            fontSize: isMobile ? "11px" : "12px",
            fontWeight: "bold",
            color: "#fff",
        },
        videoGuideImg: {
            marginLeft: "5px",
            width: "20px",
            height: "20px",
        },
        downloadTextParagraph: {
            marginTop: "30px",
            fontSize: "14px",
            color: "gray",
            fontWeight: "500",
        },
        downloadText: {
            color: "#5AC994",
            textDecoration: "none",
        },
        downloadContainer: {
            marginTop: "-20px",
            maxWidth: "400px",
            width: "100%",
            padding: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
        },
        downloadLink: {
            textDecoration: "none",
            fontSize: "14px",
            fontWeight: "600",
            color: "#5AC994",
        },
        downloadImg: {
            width: isMobile ? "80px" : "100px",
            height: "auto",
        },
        helpContainer: {
            display: "flex",
            flexDirection: "column",
            marginTop: "10px",
            justifyContent: "center",
            alignItems: "center",
        },
        support: {
            color: "#5AC994",
            fontWeight: 600,
            fontSize: "16px",
        },
        mailContainer: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: isMobile ? "10px" : "30px",
            marginTop: "-10px"
        },
        mail: {
            display: "flex",
        },
        mailLogo: {
            width: "20px",
            height: "20px",
        },
        mailInfo: {
            fontSize: "14px",
            color: "#000000",
            fontWeight: 600,
            marginLeft: "10px",
        },
        loadingBar: {
            display: "block",
            width: "100%",
            height: "5px",
            borderRadius: "5px",
            position: "relative",
            backgroundColor: "#f1f1f1",
        },
        loadingBarAfter: {
            content: "''",
            display: "block",
            width: "0",
            height: "100%",
            backgroundColor: "#4caf50",
            animation: "loading 2s infinite",
        },
        ndiButtonMobile: {
            backgroundColor: "#5AC994",
            width: "100%",
            padding: "10px",
            borderRadius: "5px",
            textDecoration: "none",
            color: "#FFFFFF",
            fontWeight: "600",
        },
        desktopH2: {
            fontSize: "24px",
            display: isMobile ? "none" : "block",
        },
        mobileH2: {
            fontSize: "24px",
            display: isMobile ? "block" : "none",
        },
        mobileNdiView: {
            marginTop: "30px",
            display: isMobile ? "block" : "none",
        },
        mobileView: {
            marginTop: "30px",
            display: isMobile ? "flex" : "block",
            flexDirection: isMobile ? "row" : "column",
            gap: "10px",
        },
        mobileViewHr: {
            width: isMobile ? "80px" : "100px",
            margin: "10px",
        },
    };
    
    return (
        <>
            {dialogueVisible && (
                <div
                    id="dialogue-box"
                    className="dialogue-box"
                    style={styles.dialogueBox}
                >
                    <div
                        className="dialogue-content"
                        style={styles.dialogueContent}
                    >
                        <span
                            className="close-button"
                            style={styles.closeButton}
                            onClick={() => ref.current.closeDialogue()}
                        >
                            &times;
                        </span>
                        <h2 style={styles.desktopH2}>
                            Scan with{" "}
                            <span style={{ color: "#5AC994" }}>Bhutan NDI</span>{" "}
                            Wallet
                        </h2>
                        <h2 style={styles.mobileH2}>
                            Login with{" "}
                            <span style={{ color: "#5AC994" }}>Bhutan NDI</span>{" "}
                            Wallet
                        </h2>
                        <div
                            className="mobile-ndi-view"
                            style={styles.mobileNdiView}
                        >
                            <div className="mobile-ndi">
                                <a
                                    id="deep-link"
                                    style={styles.ndiButtonMobile}
                                    href={deepLinkUrl}
                                >
                                    Login Bhutan NDI Wallet
                                </a>
                                <div
                                    className="mobile-view"
                                    style={styles.mobileView}
                                >
                                    <hr style={styles.mobileViewHr} />
                                    <p>OR</p>
                                    <hr style={styles.mobileViewHr} />
                                </div>
                            </div>
                        </div>
                        <div
                            className="qr_code_container"
                            style={styles.qrCodeContainer}
                        >
                            {loading ? (
                                <div
                                    id="loading-bar"
                                    className="loading-bar"
                                    style={styles.loadingBar}
                                >
                                    QR Code Generating...
                                </div>
                            ) : (
                                <>
                                    {errorMessage && (
                                        <p
                                            id="error-message"
                                            style={{
                                                display: "block",
                                                color: "red",
                                            }}
                                        >
                                            {errorMessage}
                                        </p>
                                    )}
                                    {qrCode && (
                                        <img
                                            id="qr-code"
                                            src={qrCode}
                                            alt="QR Code"
                                            style={styles.qrCodeImage}
                                        />
                                    )}
                                    <img
                                        id="ndi-logo"
                                        src={qrcode}
                                        alt="Small Image"
                                        className="small-image"
                                        style={styles.smallImage}
                                    />
                                </>
                            )}
                        </div>
                        <div
                            className="info_container"
                            style={styles.infoContainer}
                        >
                            <ol style={styles.infoOrder}>
                                <li>Open Bhutan NDI wallet on your phone</li>
                                <li>
                                    <div className="info-2">
                                        Tap the scan button
                                        <img
                                            src={uiScanIcon}
                                            alt="uiScanIcon"
                                            style={styles.videoGuideImg}
                                        />
                                        located on the menu bar and scan the QR
                                        code
                                    </div>
                                </li>
                            </ol>
                        </div>
                        <div className="video_guide" style={styles.videoGuide}>
                            <a href="#" style={styles.downloadLink}>
                                Watch video guide
                            </a>
                            <img
                                src={PlayButton}
                                alt="watch"
                                style={styles.videoGuideImg}
                            />
                        </div>
                        <p
                            className="download_text"
                            style={styles.downloadTextParagraph}
                        >
                            Don't have the Bhutan NDI wallet?{" "}
                            <a href="#" style={styles.downloadText}>
                                Download Now!
                            </a>
                        </p>
                        <div
                            className="download_container"
                            style={styles.downloadContainer}
                        >
                            <div className="download_button">
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.bhutanndi&hl=en"
                                    style={styles.downloadLink}
                                >
                                    <img
                                        src={playStore}
                                        alt="google-play"
                                        style={styles.downloadImg}
                                    />
                                </a>
                                <a
                                    href="https://apps.apple.com/au/app/bhutan-ndi/id1645493166"
                                    style={styles.downloadLink}
                                >
                                    <img
                                        src={appStore}
                                        alt="app-store"
                                        style={styles.downloadImg}
                                    />
                                </a>
                            </div>
                        </div>
                        <div
                            className="help_container"
                            style={styles.helpContainer}
                        >
                            <p style={styles.support}>Get Support</p>
                            <div style={styles.mailContainer}>
                                <div style={styles.mail}>
                                    <img
                                        style={styles.mailLogo}
                                        src={mail}
                                        alt="email icon"
                                    />
                                    <p style={styles.mailInfo}>
                                        ndifeedback@dhi.bt
                                    </p>
                                </div>
                                <div style={styles.mail}>
                                    <img
                                        style={styles.mailLogo}
                                        src={call}
                                        alt="phone icon"
                                    />
                                    <p style={styles.mailInfo}>1199</p>{" "}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ErrorModal
                showModal={showModal}
                setShowModal={() => setShowModal(!showModal)}
                modalContent={modalContent}
            />
        </>
    );
});

export default DialogueBox;
